import { useState, useEffect } from 'react'
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Calendar,
  Tag,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Loader
} from 'lucide-react'
import type { ChangelogEntry } from '../types/changelog'

export default function ChangelogManager() {
  const [entries, setEntries] = useState<ChangelogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingEntry, setEditingEntry] = useState<Partial<ChangelogEntry> | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [filterStatus, setFilterStatus] = useState<string>('all')

  useEffect(() => {
    fetchEntries()
  }, [])

  const fetchEntries = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/admin/changelog/entries', {
        credentials: 'include',
        headers
      })

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication required. Please log in again.')
        } else if (response.status === 404) {
          setError('Changelog API endpoint not found. This feature may not be available yet.')
        } else {
          setError(`Failed to load changelog entries (${response.status}). Please try again.`)
        }
        return
      }

      const data = await response.json()
      setEntries(data)
    } catch (err) {
      console.error('Error fetching changelog entries:', err)
      setError('Failed to load changelog entries. Please check your connection.')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!editingEntry?.title || !editingEntry?.content) {
      alert('Title and content are required')
      return
    }

    setSaving(true)
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const url = editingEntry.id
        ? `/api/admin/changelog/entries/${editingEntry.id}`
        : '/api/admin/changelog/entries'

      const method = editingEntry.id ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers,
        body: JSON.stringify(editingEntry)
      })

      if (!response.ok) {
        throw new Error('Failed to save changelog entry')
      }

      await fetchEntries()
      setEditingEntry(null)
      setIsCreating(false)
    } catch (err) {
      console.error('Error saving changelog entry:', err)
      alert('Failed to save changelog entry. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this changelog entry?')) {
      return
    }

    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(`/api/admin/changelog/entries/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers
      })

      if (!response.ok) {
        throw new Error('Failed to delete changelog entry')
      }

      await fetchEntries()
    } catch (err) {
      console.error('Error deleting changelog entry:', err)
      alert('Failed to delete changelog entry. Please try again.')
    }
  }

  const filteredEntries = filterStatus === 'all'
    ? entries
    : entries.filter(e => e.status === filterStatus)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Changelog Manager</h2>
          <p className="text-sm text-gray-600 mt-1">Create and manage changelog entries</p>
        </div>
        <button
          onClick={() => {
            setIsCreating(true)
            setEditingEntry({
              title: '',
              content: '',
              status: 'draft',
              category: 'announcement',
              tags: [],
              productIds: [],
              audienceType: 'all',
              severity: 'low',
              source: 'manual'
            })
          }}
          className="btn-primary flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Entry
        </button>
      </div>

      {/* Filter */}
      <div className="flex items-center space-x-4">
        <label className="text-sm font-medium text-gray-700">Filter by status:</label>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-lg border-gray-300 text-sm"
        >
          <option value="all">All</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="scheduled">Scheduled</option>
        </select>
      </div>

      {/* Editor Modal */}
      {(isCreating || editingEntry) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingEntry?.id ? 'Edit Changelog Entry' : 'New Changelog Entry'}
                </h3>
                <button
                  onClick={() => {
                    setEditingEntry(null)
                    setIsCreating(false)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={editingEntry?.title || ''}
                    onChange={(e) => setEditingEntry({ ...editingEntry, title: e.target.value })}
                    className="w-full rounded-lg border-gray-300"
                    placeholder="What's new?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={editingEntry?.description || ''}
                    onChange={(e) => setEditingEntry({ ...editingEntry, description: e.target.value })}
                    className="w-full rounded-lg border-gray-300"
                    placeholder="Brief summary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content *
                  </label>
                  <textarea
                    value={editingEntry?.content || ''}
                    onChange={(e) => setEditingEntry({ ...editingEntry, content: e.target.value })}
                    rows={8}
                    className="w-full rounded-lg border-gray-300"
                    placeholder="Detailed changelog content (supports Markdown)"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={editingEntry?.status || 'draft'}
                      onChange={(e) => setEditingEntry({ ...editingEntry, status: e.target.value as any })}
                      className="w-full rounded-lg border-gray-300"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="scheduled">Scheduled</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={editingEntry?.category || 'announcement'}
                      onChange={(e) => setEditingEntry({ ...editingEntry, category: e.target.value as any })}
                      className="w-full rounded-lg border-gray-300"
                    >
                      <option value="announcement">Announcement</option>
                      <option value="feature">Feature</option>
                      <option value="improvement">Improvement</option>
                      <option value="bugfix">Bug Fix</option>
                      <option value="security">Security</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setEditingEntry(null)
                      setIsCreating(false)
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !editingEntry?.title || !editingEntry?.content}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Entries List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Entries ({filteredEntries.length})
          </h3>

          {filteredEntries.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No changelog entries yet</p>
              <p className="text-sm text-gray-500">Create your first entry to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-base font-semibold text-gray-900">{entry.title}</h4>
                        {entry.status === 'published' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Published
                          </span>
                        )}
                        {entry.status === 'draft' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            <FileText className="h-3 w-3 mr-1" />
                            Draft
                          </span>
                        )}
                        {entry.status === 'scheduled' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            <Clock className="h-3 w-3 mr-1" />
                            Scheduled
                          </span>
                        )}
                      </div>
                      {entry.description && (
                        <p className="text-sm text-gray-600 mb-2">{entry.description}</p>
                      )}
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span className="flex items-center">
                          <Tag className="h-3 w-3 mr-1" />
                          {entry.category}
                        </span>
                        {entry.publishedAt && (
                          <span className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {new Date(entry.publishedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => setEditingEntry(entry)}
                        className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-50 rounded"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
